import { createContext, useContext, useState, useEffect } from 'react'
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db, googleProvider, APP_COLLECTION, APP_DOC } from '../lib/firebase'

const AuthContext = createContext()

/**
 * There is no hardcoded admin list any more.
 *
 * Admin used to be granted by this array, written into the user's own document
 * at sign-in — which meant the client decided who was an admin, and since a
 * trainee could write their own document, so could they. Both the app and
 * firestore.rules now read `isAdmin` from the document, and only an admin (or
 * someone with Firebase console access) can put it there. To bootstrap the
 * first admin on a fresh project, set isAdmin: true on their user document in
 * the console.
 */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const allowedDomains = ['@finder.com', '@findercrew.com']
        if (!allowedDomains.some(domain => firebaseUser.email?.endsWith(domain))) {
          await signOut(auth)
          setError('Only Finder employees can access this training.')
          setUser(null)
        } else {
          const userDocRef = doc(db, APP_COLLECTION, APP_DOC, 'users', firebaseUser.uid)
          const userDoc = await getDoc(userDocRef)

          // Sign-in writes identity, never entitlement. isAdmin, role,
          // managedBy and SEED_COURSES are absent from both branches deliberately —
          // firestore.rules rejects them from a self-write, and this is the
          // code that would otherwise be tempted to send them.
          if (!userDoc.exists()) {
            await setDoc(userDocRef, {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              createdAt: serverTimestamp(),
              lastLoginAt: serverTimestamp(),
            })
          } else {
            await setDoc(userDocRef, { lastLoginAt: serverTimestamp() }, { merge: true })
          }

          // Adopt the entitlement an admin chose before this account existed.
          //
          // `role` and `isAdmin` live on the user document, keyed by a uid that
          // does not exist until now, so an admin who wants somebody to arrive
          // as a manager has nowhere to put that but a document keyed by email.
          // pending_roles is that document, and it is admin-write only — which
          // is what makes copying out of it safe, and why it is not a field on
          // the assignment a manager can write. See firestore.rules.
          //
          // Both fields are written together because the rule compares both.
          let entitlement = null
          try {
            const key = (firebaseUser.email || '').toLowerCase()
            const pendingSnap = key
              ? await getDoc(doc(db, APP_COLLECTION, APP_DOC, 'pending_roles', key))
              : null
            entitlement = pendingSnap?.exists() ? pendingSnap.data() : null
          } catch (err) {
            // Not fatal: they sign in with whatever their document already says.
            console.error('[auth] could not read pending role:', err)
          }

          const current = userDoc.exists() ? userDoc.data() : {}
          // Set ONLY by a write that actually landed. The session's view of who
          // it is has to follow the document, not the intention: reflecting the
          // pending record locally after a failed write would light up an admin
          // console backed by a document that still says nothing, and every
          // read the rules gate on isAdmin() would then be denied. A failure
          // here is therefore a no-op that retries on the next sign-in, not a
          // half-granted session.
          let adopted = null
          if (entitlement && (
            (entitlement.role ?? null) !== (current.role ?? null) ||
            (entitlement.isAdmin === true) !== (current.isAdmin === true)
          )) {
            try {
              await setDoc(userDocRef, {
                role: entitlement.role ?? null,
                isAdmin: entitlement.isAdmin === true,
              }, { merge: true })
              adopted = entitlement
            } catch (err) {
              console.error('[auth] could not adopt the pending role:', err)
            }
          }

          // Read entitlement from the document and nowhere else, so what the
          // interface offers and what the rules will actually serve agree.
          // `isAdmin` is the field the rules test; a role of 'admin' without it
          // would light up an admin console whose every read is then denied.
          //
          // Where the pending record and the document already agree, `adopted`
          // stays null and `current` is used — it holds those same values, so
          // there is nothing to overlay.
          const data = adopted
            ? { ...current, role: adopted.role ?? null, isAdmin: adopted.isAdmin === true }
            : current
          const isAdmin = data.isAdmin === true
          const role = data.role || (isAdmin ? 'admin' : null)
          // Courses an admin has assigned. Absent is normal and means "none
          // assigned yet" — what the trainee actually sees is worked out in
          // ProgressContext, which also counts what they have started.
          const assignedTracks = Array.isArray(data.tracks) ? data.tracks : []
          const heldDegrees = Array.isArray(data.degrees) ? data.degrees : []

          // An admin can enrol somebody by email before they have ever signed
          // in, so the assignment lives beside the user document rather than
          // inside it. It wins where both exist: the assignment is the thing an
          // admin edits now, and a department on an old user document is a
          // leftover from before this existed.
          let assignment = null
          try {
            const key = (firebaseUser.email || '').toLowerCase()
            const assignmentSnap = key
              ? await getDoc(doc(db, APP_COLLECTION, APP_DOC, 'assignments', key))
              : null
            assignment = assignmentSnap?.exists() ? assignmentSnap.data() : null
          } catch (err) {
            // Not fatal: they fall back to whatever their user document says.
            console.error('[auth] could not read assignment:', err)
          }

          // An admin can name somebody's manager before they have ever signed
          // in, so the choice waits in their assignment until there is a user
          // document to put it on. Copied across here, once. The rules allow
          // this exactly when the value matches the assignment, so it is
          // adoption rather than a choice.
          //
          // The chain is adopted with it. It is what a manager's MANAGER queries
          // on, so taking the manager alone would leave this person visible one
          // level up and invisible above that. The rules permit the pair only
          // when both match the assignment, so this stays adoption rather than
          // a choice — and a failure here is not silent: the console's drift
          // report compares the account against the hierarchy and offers the
          // repair.
          const assignedChain = Array.isArray(assignment?.managerChain) ? assignment.managerChain : null
          const chainDiffers = assignedChain &&
            JSON.stringify(assignedChain) !== JSON.stringify(data.managerChain || [])
          if (assignment?.managedBy && (data.managedBy !== assignment.managedBy || chainDiffers)) {
            try {
              await setDoc(userDocRef, assignedChain
                ? { managedBy: assignment.managedBy, managerChain: assignedChain }
                : { managedBy: assignment.managedBy }, { merge: true })
            } catch (err) {
              console.error('[auth] could not adopt the assigned manager:', err)
            }
          }

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isAdmin,
            isManager: role === 'manager' && !isAdmin,
            role,
            assignedTracks,
            degrees: Array.isArray(assignment?.degrees) && assignment.degrees.length
              ? assignment.degrees
              : heldDegrees,
            department: assignment?.department || data.department || null,
          })
          setError(null)
        }
      } else {
        setUser(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const login = async () => {
    setError(null)
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (err) {
      setError('Login failed. Please try again.')
    }
  }

  const logout = async () => {
    await signOut(auth)
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
